import type { Result } from 'neverthrow';

import { apiRequest, type ApiError } from './client';

/**
 * Shipping carriers Mailchute will integrate with. The DB enum is the
 * minimal "shippable today" subset (`royalmail_cd` only at MVP);
 * everything else here is UI-only — it gates the Step 4 card grid's
 * "Coming soon" tiles per ADR 0015. Appending a new entry here adds a
 * card; making it interactive requires landing the SDK package + DB
 * enum entry + Connect dialog in a follow-up PR.
 */
export const CARRIER_KINDS = [
  'royalmail_cd',
  'royalmail_oba',
  'dpd',
  'dhl',
  'evri',
  'yodel',
  'fedex',
  'ups',
] as const;
export type CarrierKind = (typeof CARRIER_KINDS)[number];

/**
 * The subset of `CARRIER_KINDS` that the backend can actually persist
 * today. Used to type-narrow the POST body and to drive the
 * "Connect" button enabled/disabled state in the card grid.
 */
export const CONNECTABLE_CARRIER_KINDS = ['royalmail_cd'] as const;
export type ConnectableCarrierKind = (typeof CONNECTABLE_CARRIER_KINDS)[number];

export function isConnectableCarrierKind(kind: CarrierKind): kind is ConnectableCarrierKind {
  return (CONNECTABLE_CARRIER_KINDS as readonly string[]).includes(kind);
}

/**
 * Carrier-connection metadata as embedded in `GET /tenants/me` and
 * returned by `POST /carrier-connections`. Mirrors
 * `apps/api/src/handlers/{get-tenants-me,create-carrier-connection}.ts:
 * sanitise(Carrier)Connection` — never carries the credentials
 * ciphertext, which is internal to the API.
 */
export interface CarrierConnectionSummary {
  readonly carrier_connection_id: string;
  readonly kind: ConnectableCarrierKind;
  readonly status: 'active' | 'revoked';
  readonly display_name: string;
  readonly credentials_metadata?: Record<string, unknown>;
  readonly created_at: string;
  readonly revoked_at?: string;
}

/**
 * Request body for `POST /carrier-connections`. The `credentials`
 * field is carrier-specific — when a second carrier ships this
 * becomes a discriminated union (today only `royalmail_cd` exists,
 * so the union has a single member).
 */
export interface CreateCarrierConnectionRequest {
  readonly kind: 'royalmail_cd';
  readonly display_name?: string;
  readonly credentials: { readonly apiToken: string };
}

export function createCarrierConnection(
  input: CreateCarrierConnectionRequest,
): Promise<Result<CarrierConnectionSummary, ApiError>> {
  return apiRequest<CarrierConnectionSummary>({
    method: 'POST',
    path: '/carrier-connections',
    body: input,
  });
}

export function revokeCarrierConnection(
  carrierConnectionId: string,
): Promise<Result<CarrierConnectionSummary, ApiError>> {
  return apiRequest<CarrierConnectionSummary>({
    method: 'DELETE',
    path: `/carrier-connections/${encodeURIComponent(carrierConnectionId)}`,
  });
}
